// eslint-disable-next-line no-unused-vars
import React, { memo, useEffect, useState, useMemo } from 'react';
import { stringify } from 'query-string';
import { useForm } from 'react-hook-form';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { array, func, object } from 'prop-types';

import useRouter from 'Hooks/useRouter';
import useUser from 'Hooks/useUser';
import useScrollLock from 'Hooks/useScrollLock';
import FilterUsersComponent from 'Components/Users/FilterUsers';
import useAnalytics from 'Hooks/useAnalytics';

const UsersFilters = memo(({
  sortOptions,
  currentSort,
  handleMobileOrderChange,
}) => {
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [mobileSortOpen, setMobileSortOpen] = useState(false);
  const { t } = useTranslation();
  const { push, location, query } = useRouter();
  const { data: { email, loggedIn } } = useUser();
  const analytics = useAnalytics();
  const {
    register,
    control,
    reset,
    getValues,
    setValue,
  } = useForm();

  const roles = useMemo(() => [
    { value: '2', label: t('orders.roles.resellerAdmin') },
    { value: '3', label: t('orders.roles.resellerUser') },
    { value: '8', label: t('orders.roles.visitor') },
  ], []);

  useEffect(() => {
    // check if query has any filters and set them in the form
    if (Object.keys(omit(query || {}, ['order_by', 'order_direction', 'page'])).length) {
      Object.entries(query).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [email, loggedIn]);

  useScrollLock(mobileFiltersOpen);

  const handleSubmit = () => {
    setMobileFiltersOpen(false);

    const formValues = getValues();

    const queryParams = Object.entries(formValues)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, value]) => value)
      .reduce((acc, [key, value]) => {
        if (value !== 'false') {
          acc[key] = value;
        }
        return acc;
      }, {});

    const searchedValues = Object.keys(queryParams).join(', ');
    analytics.track('filter_users', { filters: searchedValues });

    push({
      location: location.pathname,
      search: stringify(queryParams)
    });
  };

  const handleClearFilters = () => {
    analytics.track('clear_filters');

    setMobileFiltersOpen(false);
    reset({
      'deleted-users': false,
      'user-role': '',
      'user-search': '',
    });

    push({
      location: location.pathname,
      search: ''
    });
  };

  const onMobileOrderChange = sort => {
    analytics.track('mobile_sort');
    setMobileSortOpen(false);
    handleMobileOrderChange(sort);
  };

  const handleClearFilter = key => {
    analytics.track('filters_clear');
    setValue(key, '');
    push({
      location: location.pathname,
      search: stringify(omit(query, [key]))
    });
  };

  return (
    <FilterUsersComponent
      register={ register }
      control={ control }
      getValues={ getValues }
      setValue={ setValue }
      onSubmit={ handleSubmit }
      onClearFilters={ handleClearFilters }
      onClearFilter={ handleClearFilter }
      roles={ roles }
      activeFilters={ omit(query || {}, ['order_by', 'order_direction', 'page']) }
      mobileFiltersOpen={ mobileFiltersOpen }
      setMobileFiltersOpen={ setMobileFiltersOpen }
      mobileSortOpen={ mobileSortOpen }
      setMobileSortOpen={ setMobileSortOpen }
      sortOptions={ sortOptions }
      currentSort={ currentSort }
      handleMobileOrderChange={ onMobileOrderChange }
    />
  );
});

UsersFilters.propTypes = {
  sortOptions: array,
  currentSort: object,
  handleMobileOrderChange: func,
};

UsersFilters.displayName = 'UsersFiltersContainer';

export default UsersFilters;
